import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Modal, Alert, Space, Button, Spin, Result } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import intl from 'react-intl-universal';
import useRoomCard from '@/hooks/useRoomCard';
import useUploadDoorLock, { CardUsage, UploadStatus } from '@/hooks/useUploadDoorLock';
import { getTimeByUtcOffset } from '@/utils/intl';
import { DATE_TIME_FORMAT } from '@/config';
import * as CardApi from '@/api/card';

import BillSettle from '@/pages/bill/settle';
import { SettleDrawerRef } from '@/pages/bill/interface';
import { isNil } from '@/utils/number';

import { LockVendor } from './config';
import { ReadCardProps } from './interface';
import { transformParamsC2PMS } from './helper';
import styles from './index.module.scss';

const ReadCardModal: React.FC<ReadCardProps> = ({
  readModalVisible,
  enabledVendor,
  ...restProps
}) => {
  const defaultCardInfo = {
    resvNo: '',
    recNo: '',
    lockNo: '',
    checkInTime: '',
    checkOutTime: '',
    roomId: 0,
    roomNo: '',
  };

  const history = useHistory();
  const settleRef = useRef<SettleDrawerRef>(null);

  const [hotelId, setHotelId] = useState(null);
  const [cardInfo, setCardInfo] = useState(defaultCardInfo);
  const [requestLoading, setRequestLoading] = useState(false);
  const [requestError, setRequestError] = useState(false);

  const {
    roomCardInfo,
    readError,
    readErrorInfo,
    reading,
    blankCard,
    resetCardStates,
    startRead,
    retryRead,
  } = useRoomCard({
    hotelId,
    vendorInfo: enabledVendor,
  });

  const { startUploadDoorInfo, endUploadDoorInfo } = useUploadDoorLock();

  // 获取酒店id(H21)
  const fetchHotelId = () => {
    return CardApi.fetchHotelId();
  };

  // 获取房卡绑定的信息
  const fetchCardInfo = async (params) => {
    if (!params?.cardNo) {
      return;
    }
    setRequestLoading(true);
    try {
      const res = await CardApi.fetchCardInfo({
        cardNo: params.cardNo,
        hotelId: params?.hotelId,
      });

      setRequestLoading(false);
      if (res && res.status) {
        setCardInfo({
          resvNo: res.data?.resvNo,
          recNo: res.data?.recNo,
          lockNo: res.data?.lockNo,
          checkInTime: res.data?.validityStartTime,
          checkOutTime: res.data?.validityEndTime,
          roomId: res.data?.roomId || 0,
          roomNo: res.data?.roomNo || '',
        });

        endUploadDoorInfo({ result: UploadStatus.Success, roomNo: res.data?.roomNo });
      } else {
        // console.log(res.msg || '获取房卡信息失败');
        setRequestError(true);

        endUploadDoorInfo({ result: UploadStatus.Fail, remark: res.msg });
      }
    } catch (e) {
      setRequestLoading(false);
      setRequestError(true);

      endUploadDoorInfo({ result: UploadStatus.Fail, remark: e.msg });
    }
  };

  useEffect(() => {
    if (blankCard) {
      // 读卡时空白卡上报
      endUploadDoorInfo({
        result: UploadStatus.Success,
        roomNo: intl.get('device_door_empty_card'),
      });
    }
  }, [blankCard]);

  useEffect(() => {
    if (readError) {
      endUploadDoorInfo({ result: UploadStatus.Fail, remark: readErrorInfo?.error });
    }
  }, [readError]);

  // 重试
  const retry = () => {
    startUploadDoorInfo({ type: CardUsage.Read });

    setRequestError(false);
    setRequestLoading(false);
    retryRead();
  };

  // 结账
  const gotoSettle = () => {
    if (restProps.readCallBack && typeof restProps.readCallBack === 'function')
      restProps.readCallBack();
    settleRef.current!.openDrawer({
      resvNo: cardInfo.resvNo,
    });
  };

  // 订单详情
  const gotoOrderDetail = () => {
    if (restProps.readCallBack && typeof restProps.readCallBack === 'function')
      restProps.readCallBack();
    setTimeout(() => {
      history.push(`/order/room-order/${cardInfo.resvNo}`);
    }, 1000);
  };

  const resetStates = () => {
    setRequestLoading(false);
    setRequestError(false);
    setHotelId(null);
    setCardInfo(defaultCardInfo);
    resetCardStates();
  };

  const closeModal = () => {
    resetStates();
    if (restProps.readCallBack && typeof restProps.readCallBack === 'function')
      restProps.readCallBack();
  };

  useEffect(() => {
    if (readModalVisible) {
      startUploadDoorInfo({ type: CardUsage.Read });

      // H21需要先获取酒店id
      if (enabledVendor?.lockCode === LockVendor.H21) {
        fetchHotelId().then((res) => {
          setHotelId(Number(res.data));
        });
      } else {
        startRead();
      }
    } else {
      resetStates();
    }
  }, [readModalVisible]);

  // 开始读卡(H21)
  useEffect(() => {
    if (!isNil(hotelId)) {
      startRead();
    }
  }, [hotelId]);

  // 成功读取到房卡
  useEffect(() => {
    if (enabledVendor?.lockCode) {
      const tempRoomCardInfo = transformParamsC2PMS(enabledVendor?.lockCode, roomCardInfo);

      fetchCardInfo({
        cardNo: tempRoomCardInfo.serialnumber,
        hotelId: enabledVendor?.lockCode === LockVendor.H21 ? tempRoomCardInfo.hotelid : undefined,
      });
    }
  }, [enabledVendor?.lockCode, roomCardInfo]);

  // reset states when unmounted
  useEffect(() => {
    return () => {
      resetStates();
    };
  }, []);

  const modalFooter = () => {
    if (blankCard || requestError || readError) {
      return (
        <Space align="start" size={16}>
          <Button type="primary" onClick={() => retry()}>
            {intl.get('app_confirm_retry_btn')}
          </Button>
          <Button onClick={() => closeModal()}>{intl.get('app_confirm_cancel_btn')}</Button>
        </Space>
      );
    }

    if (cardInfo && cardInfo.roomId !== 0) {
      return (
        <Space align="start" size={16}>
          <Button type="primary" onClick={() => gotoSettle()}>
            {intl.get('app_checkout')}
          </Button>
          <Button type="primary" onClick={() => gotoOrderDetail()}>
            {intl.get('app_order')}
          </Button>
          <Button onClick={() => closeModal()}>{intl.get('app_confirm_cancel_btn')}</Button>
        </Space>
      );
    }

    return <Button onClick={() => closeModal()}>{intl.get('app_confirm_cancel_btn')}</Button>;
  };

  const renderHint = () => {
    if (requestLoading || reading) {
      return <Alert message={intl.get('cpm_cards_put_on_write_mechine')} type="info" showIcon />;
    }
    if (requestError || readError) {
      return (
        <Alert
          message={intl.get('cpm_cards_room_card_status_abnormal_canot_read')}
          type="error"
          showIcon
        />
      );
    }

    if (blankCard || (cardInfo && cardInfo.roomId !== 0)) {
      return <Alert message={intl.get('cpm_cards_read_room_card_info')} type="success" showIcon />;
    }

    return null;
  };

  const renderCardInfo = () => {
    if (requestLoading || reading) {
      return (
        <div className={styles['read-card-loading']}>
          <Spin
            tip={intl.get('cpm_cards_info_reding')}
            indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
          />
        </div>
      );
    }

    if (requestError || readError) {
      return (
        <Result
          style={{ height: 200 }}
          status="error"
          subTitle={intl.get('cpm_cards_info_abnormal')}
        />
      );
    }

    if (blankCard) {
      return (
        <Result
          style={{ height: 200 }}
          status="success"
          subTitle={intl.get('cpm_cards_current_empty_card')}
        />
      );
    }

    if (cardInfo && cardInfo.roomId !== 0 && !requestLoading && !reading) {
      return (
        <ul className={styles.cardInfo}>
          <li>
            <label>{intl.get('app_room')}</label>
            <span>{cardInfo.roomNo || '-'}</span>
          </li>
          <li>
            <label>{intl.get('device_lock_number')}</label>
            <span>{cardInfo.lockNo || '-'}</span>
          </li>
          <li>
            <label>{intl.get('manage_license_field_start_label')}</label>
            <span>{getTimeByUtcOffset(cardInfo.checkInTime, DATE_TIME_FORMAT) || '-'}</span>
          </li>
          <li>
            <label>{intl.get('cpm_cards_end_validity_period')}</label>
            <span>{getTimeByUtcOffset(cardInfo.checkOutTime, DATE_TIME_FORMAT) || '-'}</span>
          </li>
        </ul>
      );
    }

    return null;
  };

  return (
    <Modal
      width={560}
      title={intl.get('app_read_room_card')}
      visible={readModalVisible}
      maskClosable={false}
      keyboard={false}
      footer={modalFooter()}
      // destroyOnClose
      onCancel={() => closeModal()}
    >
      {renderHint()}
      {renderCardInfo()}
      <BillSettle
        ref={settleRef}
        settleCallback={() => {
          resetStates();
        }}
      />
    </Modal>
  );
};

const mapStateToProps = (state) => {
  const { roomCardEnabled, enabledVendor } = state.card;
  return { roomCardEnabled, enabledVendor };
};

export default connect(mapStateToProps, null)(ReadCardModal);
